import {
  currencySchema,
  INSTITUTION_VAULT_KINDS,
  VAULT_OUTFLOW_DESCRIPTION_MAX_LENGTH,
  VAULT_OUTFLOW_DOCUMENT_MAX_BYTES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const descriptionSchema = z.string().trim().min(1).max(VAULT_OUTFLOW_DESCRIPTION_MAX_LENGTH);

const reasonSchema = z.string().trim().min(1).max(500);

export const outflowCategorySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
});

export type OutflowCategory = z.infer<typeof outflowCategorySchema>;

export const OUTFLOW_STATUSES = ['recorded', 'reversed'] as const;

export const vaultOutflowSchema = z.object({
  id: z.uuid(),
  vault: z.object({
    id: z.uuid(),
    kind: z.enum(INSTITUTION_VAULT_KINDS),
    name: z.string().nullable(),
  }),
  institution: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
  category: outflowCategorySchema,
  amount: moneySchema,
  description: z.string().nullable(),
  occurredOn: z.string(),
  status: z.enum(OUTFLOW_STATUSES),
  recordedByName: z.string().nullable(),
  recordedAt: z.string(),
  reversedByName: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
  document: z.object({ fileName: z.string(), available: z.boolean() }).nullable(),
});

export type VaultOutflow = z.infer<typeof vaultOutflowSchema>;

export const vaultOutflowListInputSchema = paginationInputSchema.extend({
  vaultId: z.uuid().optional(),
  institutionId: z.uuid().optional(),
  status: z.enum(OUTFLOW_STATUSES).optional(),
});

export type VaultOutflowListInput = z.infer<typeof vaultOutflowListInputSchema>;

export const requestOutflowDocumentUploadInputSchema = z.object({
  vaultId: z.uuid(),
  fileName: fileNameSchema,
  fileSize: z.number().int().min(1).max(VAULT_OUTFLOW_DOCUMENT_MAX_BYTES),
});

export type RequestOutflowDocumentUploadInput = z.infer<
  typeof requestOutflowDocumentUploadInputSchema
>;

export const recordVaultOutflowInputSchema = z.object({
  vaultId: z.uuid(),
  amountMinor: positiveMinorSchema,
  currency: currencySchema,
  categoryId: z.uuid(),
  occurredOn: isoDaySchema.optional(),
  description: descriptionSchema.optional(),
  documentUploadToken: uploadTokenSchema.optional(),
});

export type RecordVaultOutflowInput = z.infer<typeof recordVaultOutflowInputSchema>;

const outflowResultSchema = z.object({ outflow: vaultOutflowSchema });

/**
 * Money spent out of an institution's vault — docs/decisions/0133. Reads sit
 * behind `finance.view`, the permission that opens *Kasalar*; recording and
 * reversing behind `institutionVault.outflow`, both in the vault's
 * institution's scope. One person records, effective at once; a mistake is
 * reversed, never edited or deleted, so no such route exists.
 */
export const institutionVaultOutflowContract = {
  categories: oc
    .route({ method: 'GET', path: '/institution-vault-outflow-categories' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(outflowCategorySchema) })),

  list: oc
    .route({ method: 'GET', path: '/institution-vault-outflows' })
    .errors(guarded)
    .input(vaultOutflowListInputSchema)
    .output(paginated(vaultOutflowSchema)),

  /** A presigned `PUT` for the optional document, as every upload is issued — .claude/rules/file-uploads.md. */
  requestDocumentUpload: oc
    .route({ method: 'POST', path: '/institution-vault-outflows/document-uploads' })
    .errors(guarded)
    .input(requestOutflowDocumentUploadInputSchema)
    .output(presignedUploadSchema),

  record: oc
    .route({ method: 'POST', path: '/institution-vault-outflows' })
    .errors(guarded)
    .input(recordVaultOutflowInputSchema)
    .output(outflowResultSchema),

  reverse: oc
    .route({ method: 'POST', path: '/institution-vault-outflows/{id}/reverse' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(outflowResultSchema),

  /** A 60-second link to the document, only once it scanned clean, audited before it is returned. */
  documentLink: oc
    .route({ method: 'GET', path: '/institution-vault-outflows/{id}/document' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ url: z.string(), expiresAt: z.string() })),
};
